import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import NavigationBar from '../components/NavigationBar';
import PatientTabs from '../components/PatientTabs';
import usePatientSummary from '../utils/usePatientSummary';
import '../css/HealthReport.css';
import { API_BASE_URL } from '../config/config';
import axios from 'axios';

const HealthReport = ({ testData, onClose }) => {
    // 이 화면은 두 가지로 열린다.
    //   · 대시보드/관제센터 모달 → 부모가 미리 받아 온 testData 를 통째로 받는다
    //   · 탭/주소              → /health-report/:userId 에서 id 만 받는다
    // 주소로 올 때는 여기서 직접 받는다 — 모달이 부르던 것과 같은 엔드포인트다.
    const { userId } = useParams();
    // 머리말 이름과 스마트링 탭에 쓴다. 주소로 바로 열려도 대상자를 이름으로 부른다.
    const summary = usePatientSummary(userId);
    const navigate = useNavigate();
    const isModal = typeof onClose === 'function';
    const handleClose = isModal ? onClose : () => navigate(-1);

    const [fetched, setFetched] = useState(null);
    const [loadError, setLoadError] = useState(null);

    // 모달은 testData 를 받고, 주소로 오면 아래 useEffect 가 fetched 를 채운다.
    // 둘 다 없으면 아직 못 받은 것이다 — 빈 객체로 뭉개지 말고 상태를 그대로 드러낸다.
    // (예전에는 `testData || {}` 라서, 못 받았을 때도 옵셔널 체이닝이 전부 undefined 로
    //  떨어지며 오류도 안내도 없는 빈 화면을 그렸다.)
    const data = testData ?? fetched;

    useEffect(() => {
        if (testData || !userId) return;
        let cancelled = false;
        (async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(`${API_BASE_URL}/api/guardian-health-report/${userId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!cancelled) {
                    if (res.data.success) setFetched(res.data.data);
                    else setLoadError('건강보고서를 불러오는 데 실패했습니다.');
                }
            } catch (error) {
                if (!cancelled) {
                    setLoadError(error.response?.status === 404
                        ? '건강보고서 데이터가 없습니다.'
                        : '건강보고서를 불러오는 중 오류가 발생했습니다.');
                }
            }
        })();
        return () => { cancelled = true; };
    }, [testData, userId]);
    
    // 측정 신호 설정 캐시
    const [signalConfigs, setSignalConfigs] = useState({});
    
    // 디버깅을 위한 콘솔 로그
    // console.log('HealthReport testData:', testData);
    // console.log('HealthReport data:', data);
    // console.log('ComprehensiveIndicators:', data.comprehensiveIndicators);
    
    // 정상수치 정의 (실제 의학적 기준값)
    const normalValues = {
        sbp: 120,  // 수축기 혈압 정상수치
        dbp: 80,   // 이완기 혈압 정상수치
        glu: 100,  // 혈당 정상수치 (공복)
        bmi: 22    // BMI 정상수치
    };
    
    
    // console.log('Normal values:', normalValues);
    // console.log('My values:', myValues);

    // 측정 신호 설정 조회 함수
    const fetchSignalConfig = async (measureType) => {
        if (signalConfigs[measureType]) {
            return signalConfigs[measureType];
        }
        
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/measurement-signal-config`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { measure_type: measureType }
            });
            
            if (response.data && response.data.success && response.data.data) {
                const configs = response.data.data;
                setSignalConfigs(prev => ({ ...prev, [measureType]: configs }));
                return configs;
            }
        } catch (error) {
            console.error(`측정 신호 설정 조회 오류 (${measureType}):`, error);
        }
        return null;
    };

    // 값 클램핑 함수
    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

    // 막대그래프 높이 계산 헬퍼 함수
    const calculateBarPosition = (value, min, max) => {
        if (value === undefined || value === null) return 0;
        const totalRange = max - min;
        const valueInRange = value - min;
        // 값이 범위를 벗어나지 않도록 클램핑
        const clampedValue = Math.max(min, Math.min(value, max));
        const percent = ((clampedValue - min) / totalRange) * 100;
        return Math.max(0, Math.min(percent, 100)); // 0~100 사이 값 반환
    };

    // 컴포넌트 마운트 시 측정 신호 설정 로드
    useEffect(() => {
        const loadSignalConfigs = async () => {
            const measureTypes = ['BP_SYSTOLIC', 'BP_DIASTOLIC', 'BLOOD_GLUCOSE', 'BMI', 'PROTEIN_IN_URINE'];
            await Promise.all(measureTypes.map(type => fetchSignalConfig(type)));
        };
        
        loadSignalConfigs();
    }, []);

    const getStatusText = (status) => {
        switch (status) {
            case 'NORMAL': return '좋음';
            case 'WARNING': return '보통';
            case 'DANGER': return '나쁨';
            case 'UNDEFINED': return '기록 없음';
            default: return '기록 없음';
        }
    };





    // 주소로 열었을 때 헤더·탭 줄까지 그리는 껍데기. 모달에는 그리지 않는다 —
    // 대시보드가 이미 흰 상자를 씌우고, 모달 안에서 탭을 누르면 상자가 닫히지 않은 채
    // 주소만 바뀐다. 보고서 본문(.hr-content)은 880px 문서 폭을 그대로 쓰고, 이 껍데기만
    // 다른 다섯 화면과 같은 .sds-page 폭을 쓴다 — 탭 줄이 여섯 화면에서 같은 자리·같은
    // 폭이어야 하기 때문이다.
    const chrome = !isModal && (
        <div className="sds-page detail-page hr-report__chrome">
            {/* 뒤로가기는 헤더 오른쪽이 아니라 브레드크럼 자리다 — 개요·스마트링·케어플랜과 같다.
                모달에는 돌아갈 히스토리가 없으므로 이 줄 대신 hr-title 의 닫기 버튼을 쓴다. */}
            <div className="detail-page__crumb">
                <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={handleClose}>
                    <ChevronLeft size={16} aria-hidden="true" />뒤로
                </button>
            </div>

            <div className="sds-page-header">
                <div>
                    {/* 제목은 대상자다. 어느 화면인지는 아래 탭 줄이 말한다. 이름은
                        /api/patients/:id/summary 로 받는다 — 못 받으면 id 로 부른다. */}
                    <h1 className="sds-page-header__title">
                        {summary?.name || `대상자 ${userId}`}
                        <small>건강보고서</small>
                    </h1>
                </div>
            </div>

            <PatientTabs appUserId={userId} active="report" hasSmartRing={summary?.hasSmartRing === true} />
        </div>
    );

    // 주소로 들어와 아직 못 받았거나 못 받게 된 경우. 예전에는 data 가 늘 {} 라서
    // 옵셔널 체이닝이 전부 비어 떨어지며 오류도 안내도 없는 빈 화면이 나왔다.
    // 안내만 덩그러니 띄우지 않고 껍데기 안에 앉힌다 — 라우트로 온 사람에게는 상단 바도
    // 탭 줄도 그대로 있어야 다른 대상자 화면으로 넘어갈 수 있다(CarePlan.js 와 같은 규칙).
    if (loadError || !data) {
        const notice = loadError
            ? <div className="sds-notice sds-notice--error">{loadError}</div>
            : <div className="sds-notice">건강보고서를 불러오는 중입니다.</div>;
        if (isModal) return notice;
        return (
            <div className="hr-container hr-report hr-report--route">
                <NavigationBar />
                {chrome}
                <div className="hr-content">{notice}</div>
            </div>
        );
    }


    // ── 표시용 도우미 ─────────────────────────────────────────────────────────
    // 여기서는 새 값을 만들지 않는다. API 가 준 값을 화면에 놓는 모양만 정한다.
    // ⚠️ 옛 화면은 값이 없을 때 `|| 0` 으로 떨어져 "0등", "0세", "0.0%" 를 그렸다.
    //    의료 화면에서 없는 값을 0 으로 보이면 "위험이 없다"·"아주 낮다" 로 읽힌다.
    //    아래 도우미는 없는 값을 반드시 null 로 돌려주고, 화면은 그때 "—" 를 그린다.
    const has = (v) => v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v));
    const num = (v, digits = 0) => (has(v) ? Number(Number(v).toFixed(digits)) : null);
    const fmt = (v, digits = 0) => (v === null ? '—' : v.toFixed(digits));

    // 건강자료 1 의 다섯 줄. 막대 범위는 옛 화면의 y축 범위를 그대로 옮겼다.
    const METRICS = [
        { key: 'systolicBloodPressure', name: '수축기 혈압', unit: 'mmHg', min: 90, max: 140, digits: 0 },
        { key: 'diastolicBloodPressure', name: '이완기 혈압', unit: 'mmHg', min: 60, max: 90, digits: 0 },
        { key: 'fastingBloodSugar', name: '식전 혈당', unit: 'mg/dL', min: 70, max: 140, digits: 0 },
        { key: 'postMealBloodSugar', name: '식후 혈당', unit: 'mg/dL', min: 100, max: 200, digits: 0 },
        { key: 'bmi', name: '체질량지수', unit: 'BMI', min: 18, max: 30, digits: 1 }
    ];

    // 막대가 범위의 바닥에 걸려도 보이게 최소 높이를 둔다. 정확한 값은 막대 위 숫자가 말한다.
    const barHeight = (v, m) => (v === null ? 0 : Math.max(6, calculateBarPosition(v, m.min, m.max)));

    // 좋음·보통·나쁨. API 의 status 를 그대로 옮긴다.
    const STATUS_STEPS = [
        { code: 'NORMAL', label: '좋음', tone: 'normal' },
        { code: 'WARNING', label: '보통', tone: 'caution' },
        { code: 'DANGER', label: '나쁨', tone: 'danger' }
    ];

    // 최근 6개월 표의 상태 칸. 옛 화면이 "구간 무시, 상태만으로 고정 위치" 로 표시하던 것을
    // 지킨다 — 이 표시는 값의 크기가 아니라 **범주**다. 그래서 매끄러운 그러데이션 대신
    // 똑같은 세 칸으로 나누고, 점을 그 칸의 가운데에 둔다. 그러데이션 위의 점은
    // "90번째 백분위" 처럼 정밀한 값으로 읽힌다.
    const SIGNAL_SEGMENT = { NORMAL: 0, WARNING: 1, DANGER: 2 };

    const cvRaw = data.cardiovascularDiseaseRisk?.probability;
    const cvPercent = has(cvRaw) ? (Number(cvRaw) <= 1 ? Number(cvRaw) * 100 : Number(cvRaw)) : null;
    const cvYears = data.cardiovascularDiseaseRisk?.horizonYears ?? null;

    const rankNow = num(data.healthRanking?.current);
    const rankPrev = num(data.healthRanking?.lastMonth);
    // 등수는 숫자가 작을수록 좋다(1등이 가장 건강하다). 40등 → 34등은 "6등 올랐어요".
    const rankUp = rankNow !== null && rankPrev !== null ? rankPrev - rankNow : null;

    const ageReal = num(data.healthAge?.registeredAge, 1);
    const ageHealth = num(data.healthAge?.healthAge, 1);
    // 화면에 찍힌 두 숫자에서 바로 뺀다 — 따로 받은 difference 와 반올림이 달라
    // "72세 · 76.5세 · 4.4세 많아요" 처럼 한 카드 안에서 셈이 안 맞는 일을 막는다.
    const ageGap = ageReal !== null && ageHealth !== null ? Number((ageHealth - ageReal).toFixed(1)) : null;

    const lifeNow = num(data.remainingLifeExpectancy?.current, 1);
    const lifePrev = num(data.remainingLifeExpectancy?.lastMonth, 1);
    const lifeGap = lifeNow !== null && lifePrev !== null ? Number((lifeNow - lifePrev).toFixed(1)) : null;

    // 종합지표 레이더. 옛 화면은 축마다 곱하는 수가 달라(sbp×0.6, bmi×2.5) 정상 모양부터
    // 찌그러져 있었다. 각 축을 "정상치 대비 몇 배" 로 맞춰, 정상은 반듯한 마름모가 되고
    // 나의 수치가 어느 축에서 벗어났는지가 모양으로 보이게 한다.
    const RADAR_AXES = [
        { key: 'sbp', source: 'systolicBloodPressure', label: '수축기 혈압', unit: 'mmHg', digits: 0 },
        { key: 'dbp', source: 'diastolicBloodPressure', label: '이완기 혈압', unit: 'mmHg', digits: 0 },
        { key: 'glu', source: 'bloodSugar', label: '혈당', unit: 'mg/dL', digits: 0 },
        { key: 'bmi', source: 'bmi', label: 'BMI', unit: '', digits: 1 }
    ];
    const RADAR = { cx: 130, cy: 118, unit: 60, maxRatio: 1.6 };
    // 네 축 중 하나라도 값이 없으면 나의 모양은 그리지 않는다. 없는 축을 0 으로 두면
    // 그 축만 한가운데로 꺼져 "아주 낮다" 로 보인다.
    const radarMine = RADAR_AXES.map((a) => num(data.comprehensiveIndicators?.[a.source], a.digits));
    const radarComplete = radarMine.every((v) => v !== null);
    const radarPoints = (ratios) => {
        const r = ratios.map((x) => Math.min(Math.max(x, 0), RADAR.maxRatio) * RADAR.unit);
        return [
            `${RADAR.cx},${RADAR.cy - r[0]}`,
            `${RADAR.cx + r[1]},${RADAR.cy}`,
            `${RADAR.cx},${RADAR.cy + r[2]}`,
            `${RADAR.cx - r[3]},${RADAR.cy}`
        ].join(' ');
    };

    // 심뇌혈관 반원 게이지. 0~100% 전체를 한 바퀴 반으로 쓴다 — 눈금을 50% 에서 끊으면
    // 그보다 높은 대상자가 "50%" 로 꽉 찬 채 보인다. 색은 상태색이 아니라 primary 다.
    // 이 값에는 좋음·나쁨 판정이 따라오지 않으므로, 빨갛게 칠하면 없는 경보를 만든다.
    const gaugeEnd = (f) => {
        const angle = Math.PI - Math.min(Math.max(f, 0), 1) * Math.PI;
        return { x: 120 + 95 * Math.cos(angle), y: 130 - 95 * Math.sin(angle) };
    };

    const records6 = (data.healthChart2?.lastMeasurementData || [])
        .slice()
        .sort((a, b) => {
            const nameA = (a.measureTypeName || a.measureType || '').toString();
            const nameB = (b.measureTypeName || b.measureType || '').toString();
            return nameA.localeCompare(nameB, 'ko-KR', { numeric: true, sensitivity: 'base' });
        });

    const guide = data.customizedHealthManagement || {};

    return (
        // hr-report: 이 화면에만 붙는 표시. 2026-09-14 에 본문을 아티팩트("적용 예시 4.
        // 건강보고서") 구조로 다시 짜면서 옛 hr-*-section 규칙은 걷었다. 이 스타일시트를
        // 쓰는 화면은 여기 하나뿐이다(보호자 관제 모달도 이 컴포넌트를 그대로 띄운다).
        <div className={`hr-container hr-report${isModal ? '' : ' hr-report--route'}`}>
            {/* ⚠️ 상단 바는 .sds-page 의 바로 앞 형제여야 한다. NavigationBar.css 153행의
                `.navigation-bar + *` 가 아래 내용의 위 여백을 주는데, 감싸는 요소를
                하나라도 끼우면 인접 선택자가 끊긴다. */}
            {!isModal && <NavigationBar />}
            {chrome}
            <div className="hr-content">
                {/* 제목 줄은 모달에서만 그린다. 주소로 열면 위 sds-page-header 가 제목을 맡는다. */}
                {isModal && (
                    <div className="hr-title">
                        <h1>나의 건강보고서</h1>
                        <button className="sds-btn sds-btn--secondary hr-close-btn" onClick={handleClose}>닫기</button>
                    </div>
                )}

                {/* ── 개요 및 요약 ── 파란 카드. 문장은 API 의 overview.summary 를 그대로 쓴다.
                    한 줄 헤드라인은 2단계(docs/design-phase2-features.md #9). 긴 문장을 줄이지 않는다 —
                    임상 요약을 말줄임으로 잘라 보이지 않게 두는 것보다 한 줄 더 차지하는 편이 낫다. */}
                <section className="report-hero">
                    <small className="report-hero__eyebrow">개요 및 요약</small>
                    <p className="report-hero__text">
                        {data.overview?.summary || '건강보고서는 혈압, 혈당, BMI, 건강검진 결과를 종합하여 현재 건강 상태를 한눈에 보여줍니다. 이 수치를 바탕으로 심뇌혈관질환 발생 통계, 건강등수, 건강나이, 기대여생, 맞춤형 추천사항을 확인하여 건강관리에 활용하세요.'}
                    </p>
                    {(rankNow !== null || ageHealth !== null || cvPercent !== null) && (
                        <div className="report-hero__chips">
                            {rankNow !== null && (
                                <span className="report-hero__chip"><em>건강등수</em>{rankNow}등</span>
                            )}
                            {ageHealth !== null && (
                                <span className="report-hero__chip"><em>건강나이</em>{fmt(ageHealth, 1)}세</span>
                            )}
                            {cvPercent !== null && (
                                <span className="report-hero__chip">
                                    <em>심뇌혈관</em>{cvYears ? `${cvYears}년 내 ` : ''}{fmt(cvPercent, 1)}%
                                </span>
                            )}
                        </div>
                    )}
                </section>

                {/* ── 건강자료 1 ── 지표 다섯 줄 */}
                <section className="report-sec">
                    <header className="report-sec__head">
                        <h2>건강자료 1</h2>
                        <small>지난주 평균과 이번주 평균</small>
                    </header>
                    {METRICS.map((m) => {
                        const item = data.healthChart1?.[m.key] || {};
                        const prev = num(item.lastWeek, m.digits);
                        const now = num(item.current, m.digits);
                        // 차이는 막대 위에 찍힌 두 숫자에서 뺀다. 두 막대와 차이가 한 줄 안에서 셈이 맞아야 한다.
                        const delta = prev !== null && now !== null ? Number((now - prev).toFixed(m.digits)) : null;
                        const statusLabel = getStatusText(item.status);
                        return (
                            <div className="report-metric" key={m.key}>
                                <div className="report-metric__name">
                                    <b>{m.name}</b>
                                    <small>{m.unit}</small>
                                </div>
                                <div className="report-bars" aria-label={`지난주 ${fmt(prev, m.digits)}, 이번주 ${fmt(now, m.digits)}`}>
                                    <div className="report-bars__col">
                                        <span className="report-bars__value">{fmt(prev, m.digits)}</span>
                                        <span className="report-bars__track"><i className="report-bars__bar" style={{ height: `${barHeight(prev, m)}%` }} /></span>
                                        <span className="report-bars__label">지난주</span>
                                    </div>
                                    <div className="report-bars__col">
                                        <span className="report-bars__value">{fmt(now, m.digits)}</span>
                                        <span className="report-bars__track"><i className="report-bars__bar report-bars__bar--now" style={{ height: `${barHeight(now, m)}%` }} /></span>
                                        <span className="report-bars__label">이번주</span>
                                    </div>
                                </div>
                                {/* ⚠️ 차이에 빨강·파랑을 입히지 않는다(아티팩트와 다르다).
                                    올라간 것이 나쁜지는 사람에 따라 다르다 — 저혈압·저혈당인 대상자는
                                    올라가는 편이 좋다. 좋고 나쁨은 오른쪽의 좋음·보통·나쁨이 말하고,
                                    그 판정은 API 가 준다. */}
                                <div className="report-metric__diff">
                                    {delta === null ? '—' : delta > 0 ? `▲ ${fmt(delta, m.digits)}` : delta < 0 ? `▼ ${fmt(Math.abs(delta), m.digits)}` : '변화 없음'}
                                    <small>지난주 대비</small>
                                </div>
                                <div className="report-metric__desc">
                                    <div className="report-steps" aria-label={`상태: ${statusLabel}`}>
                                        {STATUS_STEPS.map((st) => (
                                            <span
                                                key={st.code}
                                                className={`report-steps__item${item.status === st.code ? ` is-on report-steps__item--${st.tone}` : ''}`}
                                            >
                                                {st.label}
                                            </span>
                                        ))}
                                    </div>
                                    {item.description && <p>{item.description}</p>}
                                </div>
                            </div>
                        );
                    })}
                </section>

                {/* ── 종합지표 · 심뇌혈관 ── 두 칸 */}
                <div className="report-two">
                    <section className="report-sec">
                        <header className="report-sec__head">
                            <h2>종합지표</h2>
                            <small>나 vs 정상 기준</small>
                        </header>
                        <div className="report-radar">
                            <svg viewBox="-24 0 308 240" className="report-radar__svg" aria-hidden="true">
                                {[0.4, 0.8, 1.2, 1.6].map((ratio) => (
                                    <polygon key={ratio} className="report-radar__grid" points={radarPoints([ratio, ratio, ratio, ratio])} />
                                ))}
                                <line className="report-radar__grid" x1={RADAR.cx} y1={RADAR.cy - RADAR.unit * RADAR.maxRatio} x2={RADAR.cx} y2={RADAR.cy + RADAR.unit * RADAR.maxRatio} />
                                <line className="report-radar__grid" x1={RADAR.cx - RADAR.unit * RADAR.maxRatio} y1={RADAR.cy} x2={RADAR.cx + RADAR.unit * RADAR.maxRatio} y2={RADAR.cy} />
                                <polygon className="report-radar__normal" points={radarPoints([1, 1, 1, 1])} />
                                {radarComplete && (
                                    <polygon
                                        className="report-radar__mine"
                                        points={radarPoints(RADAR_AXES.map((a, i) => radarMine[i] / normalValues[a.key]))}
                                    />
                                )}
                                <text className="report-radar__label" x={RADAR.cx} y="12" textAnchor="middle">수축기 혈압</text>
                                <text className="report-radar__label" x="246" y={RADAR.cy + 4} textAnchor="middle">이완기</text>
                                <text className="report-radar__label" x={RADAR.cx} y="232" textAnchor="middle">혈당</text>
                                <text className="report-radar__label" x="14" y={RADAR.cy + 4} textAnchor="middle">BMI</text>
                            </svg>
                            <div className="report-radar__vals">
                                {RADAR_AXES.map((a, i) => (
                                    <div key={a.key}>
                                        <span>{a.label}</span>
                                        <span><b>{fmt(radarMine[i], a.digits)}</b> <em>/ {normalValues[a.key]}</em></span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="report-legend">
                            <span><i className="report-legend__swatch report-legend__swatch--mine" />나의 수치</span>
                            <span><i className="report-legend__swatch report-legend__swatch--normal" />정상 기준</span>
                        </div>
                        {!radarComplete && (
                            <p className="report-sec__note">일부 수치가 없어 나의 모양은 그리지 않았어요.</p>
                        )}
                    </section>

                    <section className="report-sec">
                        <header className="report-sec__head">
                            <h2>심뇌혈관질환 발생 통계지수</h2>
                        </header>
                        <div className="report-gauge">
                            <svg viewBox="0 0 240 150" className="report-gauge__svg" aria-hidden="true">
                                <path className="report-gauge__track" d="M25,130 A95,95 0 0 1 215,130" />
                                {cvPercent !== null && cvPercent > 0 && (() => {
                                    const e = gaugeEnd(cvPercent / 100);
                                    return <path className="report-gauge__fill" d={`M25,130 A95,95 0 0 1 ${e.x.toFixed(1)},${e.y.toFixed(1)}`} />;
                                })()}
                                <text className="report-gauge__value" x="120" y="112" textAnchor="middle">
                                    {cvPercent === null ? '—' : fmt(cvPercent, 1)}
                                    {cvPercent !== null && <tspan className="report-gauge__unit" dx="3">%</tspan>}
                                </text>
                                <text className="report-gauge__caption" x="120" y="140" textAnchor="middle">
                                    {cvYears ? `향후 ${cvYears}년 이내` : '발생 확률'}
                                </text>
                                <text className="report-gauge__tick" x="22" y="148">0%</text>
                                <text className="report-gauge__tick" x="200" y="148">100%</text>
                            </svg>
                            <p className="report-sec__text">
                                {data.cardiovascularDiseaseRisk?.description || '심뇌혈관질환 발생 통계지수 설명이 없습니다.'}
                            </p>
                        </div>
                    </section>
                </div>

                {/* ── 최근 6개월 종합지표 ── */}
                <section className="report-sec">
                    <header className="report-sec__head">
                        <h2>최근 6개월 종합지표</h2>
                        <small>마지막 측정 기준</small>
                    </header>
                    {records6.length === 0 ? (
                        <p className="report-sec__note">최근 6개월 측정 기록이 없습니다.</p>
                    ) : (
                        <>
                            <div className="report-row6 report-row6--head">
                                <span>일자</span><span>항목</span><span>수치</span><span>나의 건강 상태</span>
                            </div>
                            {records6.map((record, index) => {
                                const seg = SIGNAL_SEGMENT[record.signalType];
                                return (
                                    <div className="report-row6" key={index}>
                                        <span className="report-row6__date">{record.date || record.measureDate}</span>
                                        <span>{record.measureTypeName || record.measureType}</span>
                                        <span className="report-row6__value">{record.currentValue} {record.dataUnitName}</span>
                                        <div>
                                            {seg === undefined ? (
                                                <span className="report-row6__none">기록 없음</span>
                                            ) : (
                                                <>
                                                    <div className="report-track" aria-label={`상태: ${record.signalTypeName || ['정상', '주의', '위험'][seg]}`}>
                                                        <i className="report-track__seg report-track__seg--normal" />
                                                        <i className="report-track__seg report-track__seg--caution" />
                                                        <i className="report-track__seg report-track__seg--danger" />
                                                        <i className="report-track__dot" style={{ left: `${(seg * 2 + 1) * (100 / 6)}%` }} />
                                                    </div>
                                                    <div className="report-track__labels"><span>정상</span><span>주의</span><span>위험</span></div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </section>

                {/* ── 건강등수 · 건강나이 · 기대여생 ── 큰 숫자 세 장 */}
                <div className="report-three">
                    <div className="report-kpi">
                        <div className="report-kpi__label">이번달 건강등수</div>
                        <div className="report-kpi__value">{rankNow === null ? '—' : rankNow}{rankNow !== null && <small>등</small>}</div>
                        {rankUp !== null && (
                            <div className="report-kpi__note">
                                {rankUp > 0 && <>지난달 {rankPrev}등보다 <b className="is-good">{rankUp}등 올랐어요</b></>}
                                {rankUp < 0 && <>지난달 {rankPrev}등보다 <b className="is-bad">{Math.abs(rankUp)}등 내려갔어요</b></>}
                                {rankUp === 0 && <>지난달과 같아요</>}
                            </div>
                        )}
                        {rankPrev !== null && rankNow !== null && (
                            <div className="report-kpi__cmp"><span>지난달 <b>{rankPrev}등</b></span><span>이번달 <b>{rankNow}등</b></span></div>
                        )}
                    </div>

                    <div className="report-kpi">
                        <div className="report-kpi__label">나의 건강나이</div>
                        <div className="report-kpi__value">{fmt(ageHealth, 1)}{ageHealth !== null && <small>세</small>}</div>
                        {ageGap !== null && (
                            <div className="report-kpi__note">
                                {ageGap > 0 && <>주민등록 나이보다 <b className="is-bad">{fmt(ageGap, 1)}세 많아요</b></>}
                                {ageGap < 0 && <>주민등록 나이보다 <b className="is-good">{fmt(Math.abs(ageGap), 1)}세 적어요</b></>}
                                {ageGap === 0 && <>주민등록 나이와 같아요</>}
                            </div>
                        )}
                        {ageReal !== null && ageHealth !== null && (
                            <div className="report-kpi__cmp"><span>실제 <b>{fmt(ageReal, 1)}세</b></span><span>건강 <b>{fmt(ageHealth, 1)}세</b></span></div>
                        )}
                    </div>

                    <div className="report-kpi">
                        <div className="report-kpi__label">기대여생</div>
                        <div className="report-kpi__value">{fmt(lifeNow, 1)}{lifeNow !== null && <small>년</small>}</div>
                        {lifeGap !== null && (
                            <div className="report-kpi__note">
                                {lifeGap > 0 && <>지난달보다 <b className="is-good">{fmt(lifeGap, 1)}년 늘었어요</b></>}
                                {lifeGap < 0 && <>지난달보다 <b className="is-bad">{fmt(Math.abs(lifeGap), 1)}년 줄었어요</b></>}
                                {lifeGap === 0 && <>지난달과 같아요</>}
                            </div>
                        )}
                        {lifePrev !== null && lifeNow !== null && (
                            <div className="report-kpi__cmp"><span>지난달 <b>{fmt(lifePrev, 1)}년</b></span><span>이번달 <b>{fmt(lifeNow, 1)}년</b></span></div>
                        )}
                    </div>
                </div>

                {/* ── 맞춤형 건강관리 정보 ── 아티팩트에는 없지만 API 가 주는 실제 내용이라 남긴다. */}
                <section className="report-sec">
                    <header className="report-sec__head"><h2>맞춤형 건강관리 정보</h2></header>
                    <div className="report-guide">
                        <h3>오늘의 식이 가이드</h3>
                        <p>{guide.dietGuide || '—'}</p>
                    </div>
                    <div className="report-guide">
                        <h3>오늘의 생활 가이드</h3>
                        <p>{guide.exerciseGuide || '—'}</p>
                    </div>
                    <div className="report-guide">
                        <h3>나의 건강보조 가이드</h3>
                        {(guide.supplementGuide || []).length === 0
                            ? <p>—</p>
                            : <ul>{guide.supplementGuide.map((item, index) => <li key={index}>{item}</li>)}</ul>}
                    </div>
                    <div className="report-guide">
                        <h3>전문가 코멘트</h3>
                        {(guide.expertComments || []).length === 0
                            ? <p>—</p>
                            : <ul>{guide.expertComments.map((comment, index) => <li key={index}>{comment}</li>)}</ul>}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default HealthReport;
